import React, { useState, useEffect, useContext } from 'react';
import { getUsers, deleteUser } from '../api/api';
import UserForm from '../components/UserForm';
import { AuthContext } from '../context/AuthContext';
import '../index.css';
import './Users.css';
import Table from '../components/common/Table'; // Importar Table
import Header from '../components/common/Header';
import Search from '../components/common/Search';
import Button from '../components/common/Button';
import Error from '../components/common/Error';
import FichaContainer from '../components/oncology/FichaContainer';

import PatientGroups from './PatientGroups';

const Users = () => {
    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [userToEdit, setUserToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState(''); // Estado para el buscador
    const [showFicha, setShowFicha] = useState(false);
    const [userToFicha, setUserToFicha] = useState(null);

    const { userRole } = useContext(AuthContext);

    const canCreate = userRole === "Director" || userRole === "Navegador";
    const canUpdate = userRole === "Director" || userRole === "Navegador";
    const canDelete = userRole === "Director" || userRole === "Navegador";
    const canRead = userRole === "Director" || userRole === "Navegador" || userRole === "Lector";

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const usersData = await getUsers();
                setUsers(usersData);
            } catch (err) {
                setError(err.message || 'Error al cargar los usuarios');
            } finally {
                setLoading(false);
            }
        };
        if (canRead) {
            fetchUsers();
        } else {
            setError("No tienes permisos para ver los usuarios")
        }

    }, [canRead, userRole]); // Se han añadido las dependencias

    const handleCreate = () => {
        setUserToEdit(null);
        setShowForm(true);
    };

    const handleEdit = (user) => {
        setUserToEdit(user);
        setShowForm(true);
    };

    const handleDelete = async (email) => {
        try {
            await deleteUser(email);
            setUsers(users.filter(user => user.email !== email));
        } catch (err) {
            setError(err.message || 'Error al eliminar el usuario');
        }
    };
    const handleCloseForm = () => {
        setShowForm(false);
        setUserToEdit(null);
    }

    const handleUserUpdated = (newUser) => {
        setUsers(users.map((user) => (user.email === newUser.email ? newUser : user)))
    }

    const handleUserCreated = (newUser) => {
        setUsers([...users, newUser])
    }

    // Ficha Handlers
    const handleFicha = (user) => {
        setUserToFicha(user);
        setShowFicha(true);
    };

    const handleCloseFicha = () => {
        setShowFicha(false);
        setUserToFicha(null);
    };

    // Función para filtrar los usuarios
    const filteredUsers = users.filter((user) => {
        const userValues = Object.values(user).join('').toLowerCase();
        return userValues.includes(searchTerm.toLowerCase());
    });

    if (loading) return <div>Cargando...</div>;
    if (error) return <Error error={error} />;

    const tableHeaders = ["Email", "Nombre", "Apellido", "Rol", "Step", "Acciones"];

    // Función para truncar el texto
    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + "...";
        }
        return text;
    };

    const renderUserRow = (user) => [
        user.email,
        truncateText(user.first_name, 30),
        truncateText(user.last_name, 30),
        user.role,
        user.step,
        <div className="actions-cell">
            {canUpdate ? <button className="edit-btn" onClick={() => handleEdit(user)}>Editar</button> : null}
            {canDelete ? <button className="delete-btn" onClick={() => handleDelete(user.email)}>Eliminar</button> : null}
            <button className="ficha-btn" onClick={() => handleFicha(user)}>Ficha</button>
        </div>
    ];

    const renderTabs = () => (
        <div className="tabs-container">
            <button
                className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
            >
                👥 Usuarios
            </button>
            <button
                className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
                onClick={() => setActiveTab('groups')}
            >
                🏥 Grupos de Pacientes
            </button>
        </div>
    );

    return (
        <div className='container users-container'>
            <Header title="Gestión de Usuarios y Grupos" />

            {renderTabs()}

            {activeTab === 'users' ? (
                <>
                    <div className="user-controls">
                        <Search searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                        {canCreate && <button className="create-user-btn" onClick={handleCreate}>➕ Crear Usuario</button>}
                    </div>

                    {showForm && (
                        <div className="modal-overlay" onClick={handleCloseForm}>
                            <div className='container-form' onClick={e => e.stopPropagation()}>
                                <UserForm
                                    onClose={handleCloseForm}
                                    user={userToEdit}
                                    onUserUpdated={handleUserUpdated}
                                    onUserCreated={handleUserCreated}
                                />
                            </div>
                        </div>
                    )}

                    {showFicha && (
                        <div className="modal-overlay" onClick={handleCloseFicha}>
                            <div onClick={e => e.stopPropagation()}>
                                <FichaContainer user={userToFicha} onClose={handleCloseFicha} />
                            </div>
                        </div>
                    )}

                    <div className="table-responsive">
                        <Table headers={tableHeaders} data={filteredUsers} renderRow={renderUserRow} />
                    </div>
                </>
            ) : (
                <PatientGroups />
            )}
        </div>
    );
};

export default Users;
